import {
  isAngebotsIdEqual,
  QuoteDbCheckoutPayload,
  QuoteDbDashboardPayload,
  QuoteDbInitPayload,
  QuoteDbSpartePayload,
  QuoteDbToolbarPayload
} from '@ps20-frontend/shop-integration-redux';
import {
  Dialogschritt,
  FahrradWert,
  Feature,
  Produktlinienname,
  QuoteDeltafaktor,
  QuoteErsatzgeschaeftInfo,
  QuoteMetadaten,
  QuoteSparte,
  QuoteSpartenDaten,
  QuoteTkvDaten,
  Selbstbeteiligung,
  SpartenType,
  TaasRequestModel,
  TaasRsData,
  TaasSalesProductName,
  TaasSparte,
  TaasVerkehrBaustein,
  TaasVhvData,
  TaasTarifrisikoschluesselVWGV2,
  TarifierungsVektor,
  VersicherungsNehmerOuote,
  Warenkorb,
  WarenkorbProdukt,
  Zahlungsart,
  Zahlweise
} from '@ps20/bff-models';
import {
  App,
  convertProduktNameToTaasProduktName,
  Gefahr,
  getSpartenTypeByApp,
  getTarifbereich,
  isAppInList,
  Leistung,
  Produktlinie,
  QuoteDbVorteilrabattItem,
  TarifierungsVektorQuoteDB,
  VermietetesObjektQuoteDB,
  ZusatzoptionItem
} from '@ps20-frontend/frontend-models';
import { TaasData } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../../localStore';

export function mapWarenkorb(
  quoteDbDataDashboard: QuoteDbDashboardPayload,
  quoteDbSparten: Array<QuoteDbSpartePayload<TaasData>>,
): Warenkorb {
  const mappedWarenkorbProdukte = mapWarenKorbProdukte(quoteDbSparten);
  const gesamtPreis: number = mappedWarenkorbProdukte
    .map(warenkorbProdukt => warenkorbProdukt.gesamtPreis)
    .reduce((a, b) => a + b, 0);

  return {
    bruttoWarenkorb: gesamtPreis,
    kombinachlassTacho: mapKombinachlass(quoteDbDataDashboard.kombinachlass),
    ersparnisTacho: quoteDbDataDashboard.ersparnis,
    anzahlProdukteUnfall: quoteDbDataDashboard.unfallZaehlstuecke,
    warenkorbProdukte: mappedWarenkorbProdukte,
  };
}

export function mapWarenKorbProdukte(
  quoteDbSparten: Array<QuoteDbSpartePayload<TaasData>>,
): WarenkorbProdukt[] {
  return quoteDbSparten.map(sparte => {
    return {
      spartenType: getSpartenTypeByApp(sparte.angebotsId.appId),
      angebotsId: sparte.angebotsId.angebotsId,
      gesamtPreis: sparte.gesamtpreis,
      gesamtPreisMitRabatt: sparte.gesamtpreisMitRabatt,
      antragsnummer: sparte.antragsnummer,
    };
  });
}

export function mapMetadaten(
  quoteDbDataToolbar: QuoteDbToolbarPayload,
  action: QuoteDbInitPayload,
  quoteDbDataCheckout: QuoteDbCheckoutPayload,
  state: IAppState,
  isGeschaeftsFuehrerRs: boolean,
): QuoteMetadaten {
  const versicherungsNehmerOuote: VersicherungsNehmerOuote = quoteDbDataToolbar.versicherungsNehmer
    ? quoteDbDataToolbar.versicherungsNehmer
    : undefined;
  if (versicherungsNehmerOuote) {
    versicherungsNehmerOuote.isGeschaeftsfuehrer = isGeschaeftsFuehrerRs;
  }
  return {
    versicherungsNehmer: versicherungsNehmerOuote,
    fehlereintragVorhanden: action.fehlerCodeTaas,
    vtnr: quoteDbDataCheckout.vtnr,
    correlationId: state.saveProtocol.correlationId,
  };
}

export function mapSpartenDaten<T extends TaasData>(
  quoteDbSparten: Array<QuoteDbSpartePayload<T>>,
  quoteDbDataToolbar: QuoteDbToolbarPayload,
  quoteDbDataDashboard: QuoteDbDashboardPayload,
  quoteDbDataCheckout: QuoteDbCheckoutPayload,
  action: QuoteDbInitPayload,
  state: IAppState,
): QuoteSpartenDaten<TaasSparte<unknown>, QuoteTkvDaten>[] {
  return quoteDbSparten.map(sparte => {
    const spartenDaten: QuoteSpartenDaten<TaasSparte<unknown>, QuoteSparte> = {
      taasRequestModel: sparte.taasSparteRequestModel,
      tarifierungsVektor: mapTarifierungsVektor(
        sparte,
        quoteDbDataToolbar,
        quoteDbDataDashboard,
        action,
        state,
      ),
      ersatzgeschaeftInfo: mapErsatzgeschaeftInfo(sparte, quoteDbDataDashboard),
      angebotsId: sparte.angebotsId.angebotsId,
    };
    const spartenQuoteDaten = quoteDbDataCheckout.quoteCheckoutDaten?.find(
      checkoutDaten =>
        checkoutDaten.appId === sparte.angebotsId.appId &&
        checkoutDaten.angebotsId === sparte.angebotsId.angebotsId,
    );
    spartenDaten.sofortschutzEnde = spartenQuoteDaten?.sofortschutzEnde;
    if (isAppInList(sparte.angebotsId.appId, [App.TKV, App.TKV_V2])) {
      let sparteTkvDaten = quoteDbDataCheckout.quoteTkvDaten.find(
          app => app.angebotsId === sparte.angebotsId.angebotsId,
        );
      if (sparte.angebotsId.appId === App.TKV_V2) {
        sparteTkvDaten = quoteDbDataCheckout.quoteTkvV2Daten.find(
          app => app.angebotsId === sparte.angebotsId.angebotsId,
        );
      }
      spartenDaten.quoteSparte = {
        spartenType: sparte.angebotsId.appId === App.TKV ? SpartenType.TIERKRANKEN : SpartenType.TIERKRANKEN_V2,
        tiername: sparteTkvDaten?.tiername,
        tierident: sparteTkvDaten?.tierident,
        einsatzgebiet: sparteTkvDaten?.einsatzgebiet,
      } as QuoteTkvDaten;
    }

    if (sparte.angebotsId.appId === App.UNF) {
      const unfAngebotsDaten = quoteDbDataCheckout.quoteUnfallDaten.find(
        unfallDaten => unfallDaten.angebotsId === sparte.angebotsId.angebotsId,
      );
      spartenDaten.quoteSparte = {
        spartenType: SpartenType.UNFALL,
        ...unfAngebotsDaten,
      };
    }

    return spartenDaten;
  });
}

export function mapErsatzgeschaeftInfo<T extends TaasData>(
  selectedSparte: QuoteDbSpartePayload<T>,
  quoteDbDataDashboard: QuoteDbDashboardPayload,
): QuoteErsatzgeschaeftInfo {
  const selectedAngebotsId = selectedSparte.angebotsId;
  const dashboardApp = quoteDbDataDashboard.selectedApps.find(dashboardItem =>
    isAngebotsIdEqual(dashboardItem, selectedAngebotsId),
  );

  if (!dashboardApp) {
    return null;
  }
  const dashboardErsatzInfo = dashboardApp.ersatzInfo;
  const isErsatz = dashboardErsatzInfo.isErsatz;
  const info: QuoteErsatzgeschaeftInfo = {
    isErsatz,
    beibehalten: dashboardErsatzInfo.beibehalten,
  };

  if (isErsatz) {
    info.jahrespraemieNettoAlt = dashboardErsatzInfo.jahresNettoVorvertrag;

    const kuendigungLiegtVor =
      selectedSparte.sparteErsatzInfo != null && selectedSparte.sparteErsatzInfo.kuendigungLiegtVor;
    info.kuendigungLiegtVor = kuendigungLiegtVor || false;

    if (selectedSparte.sparteErsatzInfo?.deltafaktoren) {
      info.deltafaktoren = selectedSparte.sparteErsatzInfo.deltafaktoren.map(
        df =>
          ({
            bausteinSchluessel: df.baustein,
            deltafaktor: df.faktor,
          }) as QuoteDeltafaktor,
      );
    }
    if (!dashboardErsatzInfo.beibehalten && selectedSparte.sparteErsatzInfo) {
      info.salesProdukt = selectedSparte.sparteErsatzInfo.salesProdukt;
      info.vorgangsId = selectedSparte.sparteErsatzInfo.vorgangsId;
      info.kurzbezeichnung = selectedSparte.sparteErsatzInfo.kurzbezeichnung;
    }
  }
  return info;
}

export function mapTarifierungsVektor<T extends TaasData>( // NOSONAR
  selectedSparte: QuoteDbSpartePayload<T>,
  quoteDbDataToolbar: QuoteDbToolbarPayload,
  quoteDbDataDashboard: QuoteDbDashboardPayload,
  action: QuoteDbInitPayload,
  state: IAppState,
): TarifierungsVektor[] {
  const selectedAngebotsId = selectedSparte.angebotsId;
  const treueBonusSparte = quoteDbDataToolbar.treuebonus?.find(tbs =>
    isAngebotsIdEqual(tbs, selectedAngebotsId),
  );
  const rbdSparte = quoteDbDataToolbar.rbd?.find(rbd => isAngebotsIdEqual(rbd, selectedAngebotsId));
  const beibehaltung = selectedSparte.taasSparteRequestModel?.beibehalten;
  const vorteilsrabattActive = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  const vorteilsrabatt: QuoteDbVorteilrabattItem = quoteDbDataToolbar.vorteilsRabatte?.find(
    vorteilsRabatt => isAngebotsIdEqual(vorteilsRabatt, selectedAngebotsId),
  );

  const zahlweise: Zahlweise = quoteDbDataToolbar.zahlweise;
  const zahlungsArt: Zahlungsart = quoteDbDataToolbar.zahlungsart;
  const selbstbeteiligung: Selbstbeteiligung = selectedSparte?.selbstbeteiligung;

  const tarifierungsVector: TarifierungsVektor[] = [];

  let verkehrBaustein: TaasVerkehrBaustein = null;
  let vermieterBausteine: Array<VermietetesObjektQuoteDB> = null;

  if (selectedAngebotsId.appId === App.RS || selectedAngebotsId.appId === App.RS_VERMIETUNG) {
    const taasRequestModelRs: TaasRequestModel<TaasRsData> =
      selectedSparte && (selectedSparte.taasSparteRequestModel as TaasRequestModel<TaasRsData>);

    verkehrBaustein = taasRequestModelRs?.taasSparte?.verkehrBaustein;
    vermieterBausteine = selectedSparte?.vermietetesObjekt;
  }
  const tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T> = {
    selectedProduktlinie: selectedSparte?.selectedProduktlinie,
    treuebonus_aktuell: treueBonusSparte && treueBonusSparte.treuebonus_aktuell,
    treuebonus_max: treueBonusSparte && treueBonusSparte.treuebonus_max,
    rdbMax: rbdSparte && rbdSparte.max,
    rbdSparte: rbdSparte && rbdSparte.aktuell,
    laufZeitSparte: selectedSparte ? selectedSparte.vertragslaufzeit : null,
    zahlweise,
    selbstbeteiligung,
    zahlungsArt,
    kombinachlass: quoteDbDataDashboard.kombinachlass,
    fahrzeugeVersichert: verkehrBaustein?.fahrzeugeVersichert,
    fahrzeugeArt: verkehrBaustein ? verkehrBaustein.fahrzeugeArt : null,
    vermieterBausteine,
    dialogschritt: action.dialogschritt,
    alleProduktlinien: selectedSparte ? selectedSparte.alleProduktlinien : null,
    taasSparteRequestModel: selectedSparte && selectedSparte.taasSparteRequestModel,
    tarifbereich: getTarifbereich({ isAssekuranztarif: quoteDbDataToolbar.isAssekuranztarif }),
    vorteilsrabatt,
    beibehaltung,
    vorteilsrabattActive,
  };

  mapTarifierungsVektorSparte(tarifierungsVektorQuoteDb).forEach(v => tarifierungsVector.push(v));

  return tarifierungsVector;
}

export function mapTarifierungsVektorSparte<T extends TaasData>(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T>,
): TarifierungsVektor[] {
  const tarifierungsVector: TarifierungsVektor[] = [];
  if (!tarifierungsVektorQuoteDb.selectedProduktlinie) {
    return tarifierungsVector;
  }

  if (tarifierungsVektorQuoteDb.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
    pushAllTarifierungsVectors(
      tarifierungsVektorQuoteDb,
      tarifierungsVektorQuoteDb.alleProduktlinien,
      tarifierungsVector,
    );
    pushHausTechnikSchutzVektorForVwgV2(tarifierungsVektorQuoteDb, tarifierungsVector);
    if (tarifierungsVektorQuoteDb.vermieterBausteine !== null) {
      pushTarifierungsVectorVermieterWohnung(
        tarifierungsVektorQuoteDb,
        tarifierungsVektorQuoteDb.selectedProduktlinie.zusatzoptionen.find(
          zusatzoption => zusatzoption.name === 'VERMIETER',
        ),
        tarifierungsVektorQuoteDb.vermieterBausteine,
        tarifierungsVector,
        tarifierungsVektorQuoteDb.alleProduktlinien,
      );
    }
  } else {
    let onlySelectedFachbaustein = true;

    if (isUnfallVektor(tarifierungsVektorQuoteDb)) {
      onlySelectedFachbaustein = false; // Für Unfall müssen auch abgewählte Bausteine getracked werden

      pushTarifierungsVector(
        tarifierungsVektorQuoteDb,
        tarifierungsVektorQuoteDb.selectedProduktlinie.leistungen as unknown as ZusatzoptionItem[],
        tarifierungsVector,
        onlySelectedFachbaustein,
      );
    }

    pushTarifierungsVector(
      tarifierungsVektorQuoteDb,
      tarifierungsVektorQuoteDb.selectedProduktlinie.gefahren as unknown as ZusatzoptionItem[],
      tarifierungsVector,
    );

    pushTarifierungsVector(
      tarifierungsVektorQuoteDb,
      tarifierungsVektorQuoteDb.selectedProduktlinie.zusatzoptionen,
      tarifierungsVector,
      onlySelectedFachbaustein,
    );

    pushHausTechnikSchutzVektorForVwgV2(tarifierungsVektorQuoteDb, tarifierungsVector);

    if (tarifierungsVektorQuoteDb.vermieterBausteine !== null) {
      pushTarifierungsVectorVermieterWohnung(
        tarifierungsVektorQuoteDb,
        tarifierungsVektorQuoteDb.selectedProduktlinie.zusatzoptionen.find(
          zusatzoption => zusatzoption.name === 'VERMIETER',
        ),
        tarifierungsVektorQuoteDb.vermieterBausteine,
        tarifierungsVector,
        [tarifierungsVektorQuoteDb.selectedProduktlinie],
      );
    }
  }

  return tarifierungsVector;
}

function pushTarifierungsVector(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<TaasData>,
  fachbausteine: ZusatzoptionItem[],
  tarifierungsVector: TarifierungsVektor[],
  onlySelected = true,
): void {
  fachbausteine
    .filter(fachbaustein => fachbaustein.name !== 'VERMIETER')
    ?.forEach(fachbaustein => {
      if (!onlySelected || fachbaustein.isSelected) {
        tarifierungsVector.push({
          ...mapFachbaustein(
            tarifierungsVektorQuoteDb.selectedProduktlinie,
            fachbaustein,
            tarifierungsVektorQuoteDb,
          ),
        });
      }
    });
}

function pushAllTarifierungsVectors<T extends TaasData>(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T>,
  alleProduktlinien: Produktlinie[],
  tarifierungsVector: TarifierungsVektor[],
): void {
  alleProduktlinien.forEach(produktlinie => {
    // NOSONAR
    produktlinie.gefahren.forEach(gefahr => {
      const baustein = mapFachbaustein(produktlinie, gefahr, tarifierungsVektorQuoteDb);
      tarifierungsVector.push(baustein);
    });

    produktlinie.zusatzoptionen
      .filter(zusatzoption =>
        tarifierungsVektorQuoteDb.vermieterBausteine !== null
          ? zusatzoption.name !== 'VERMIETER'
          : zusatzoption.name !== '',
      )
      .forEach(zusatzoption => {
        tarifierungsVector.push({
          ...mapFachbaustein(produktlinie, zusatzoption, tarifierungsVektorQuoteDb),
        });
      });

    if (isUnfallVektor(tarifierungsVektorQuoteDb)) {
      produktlinie.leistungen.forEach(zusatzoption => {
        tarifierungsVector.push({
          ...mapFachbaustein(produktlinie, zusatzoption, tarifierungsVektorQuoteDb),
        });
      });
    }
  });
}

function pushTarifierungsVectorVermieterWohnung(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<TaasData>,
  fachbaustein: ZusatzoptionItem,
  vermieterBausteine: VermietetesObjektQuoteDB[],
  tarifierungsVector: TarifierungsVektor[],
  produktlinien: Produktlinie[],
): void {
  produktlinien?.forEach(produktlinie => {
    vermieterBausteine?.forEach(vermieterBaustein => {
      tarifierungsVector.push({
        ...mapFachbaustein(produktlinie, fachbaustein, tarifierungsVektorQuoteDb),
        nummerSachort: vermieterBaustein.id,
        plzRisikoOrt: vermieterBaustein.plzSachort,
        einheitenArt: vermieterBaustein.einheitenArt,
        anzahlVermieteteEinheiten: vermieterBaustein.anzahlVermieteteEinheiten,
        jahresBruttoMieteVm: vermieterBaustein.jahresBruttoMiete,
        unbebauteFlaeche: vermieterBaustein.unbebauteFlaeche,
      });
    });
  });
}

const HTS_LEISTUNG_ID = 'hausTechnikSchutz';
const HTS_TAAS_SCHLUESSEL = TaasTarifrisikoschluesselVWGV2.GEBAEUDETECHNIK;

function pushHausTechnikSchutzVektorForVwgV2<T extends TaasData>(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T>,
  tarifierungsVector: TarifierungsVektor[],
): void {
  const alleProduktlinien = tarifierungsVektorQuoteDb.alleProduktlinien;
  if (!alleProduktlinien) {
    return;
  }

  const komfortLinie = alleProduktlinien.find(p => p.produktName === Produktlinienname.KOMFORT);
  const komfortHts = komfortLinie?.leistungen?.find(l => l.id === HTS_LEISTUNG_ID);
  if (komfortHts && !komfortHts.abgewaehlt) {
    tarifierungsVector.push(
      mapFachbaustein(komfortLinie, { ...komfortHts, name: HTS_TAAS_SCHLUESSEL, isSelected: true } as unknown as ZusatzoptionItem, tarifierungsVektorQuoteDb),
    );
  }
}

export function mapKombinachlass(kombinachlassTacho: number): number {
  if (kombinachlassTacho < 2) {
    return 0;
  }
  if (kombinachlassTacho >= 5) {
    return 20;
  }
  return {
    2: 5,
    3: 10,
    4: 15,
  }[kombinachlassTacho];
}

export function getSelectedVersicherungsSummeRadVhv(
  vhvSparte: TaasVhvData,
  selectedLinieProduktname: Produktlinienname,
): FahrradWert {
  const taasSalesProduktName: TaasSalesProductName =
    convertProduktNameToTaasProduktName(selectedLinieProduktname);
  return vhvSparte.versicherungssummenRad[taasSalesProduktName];
}

export function getSelectedVersicherungsSummeVhv(
  vhvSparte: TaasVhvData,
  selectedLinieProduktname: Produktlinienname,
): number {
  const taasSalesProduktName: TaasSalesProductName =
    convertProduktNameToTaasProduktName(selectedLinieProduktname);
  return vhvSparte.versicherungssumme[taasSalesProduktName];
}

export function getEntschaedigungsGrenzeWertsachenBetragVhv(
  vhvSparte: TaasVhvData,
  selectedLinieProduktname: Produktlinienname,
): number {
  const taasSalesProduktName: TaasSalesProductName =
    convertProduktNameToTaasProduktName(selectedLinieProduktname);
  return vhvSparte.entschaedigungsgrenzeWertsachenBetrag[taasSalesProduktName];
}

export function isTaasVhvData(val: TaasData): val is TaasVhvData {
  return val?.spartenType === SpartenType.HAUSRAT;
}

export function isUnfallVektor<T extends TaasData>(
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T>,
): boolean {
  return tarifierungsVektorQuoteDb.selectedProduktlinie.kurztext?.startsWith('UF');
}

export function mapFachbaustein<T extends TaasData>(
  produktlinie: Produktlinie,
  fachbaustein: ZusatzoptionItem | Gefahr | Leistung,
  tarifierungsVektorQuoteDb: TarifierungsVektorQuoteDB<T>,
): TarifierungsVektor {
  let baustein: TarifierungsVektor = {
    ...tarifierungsVektorQuoteDb.vorteilsrabatt,
    kennungNeuesRabattkonzept:
      produktlinie.preisverzeichnis >= 30 ||
      (!tarifierungsVektorQuoteDb.beibehaltung && tarifierungsVektorQuoteDb.vorteilsrabattActive),
    salesProdukt: produktlinie.kurztext,
    tariflicheBausteinKennung: fachbaustein.name,
    preisverzeichnis: produktlinie.preisverzeichnis,
    nachlassTreue: tarifierungsVektorQuoteDb.treuebonus_aktuell,
    nachlassAktion: produktlinie.nachlass,
    treueMax: tarifierungsVektorQuoteDb.treuebonus_max,
    rdbMax: tarifierungsVektorQuoteDb.rdbMax,
    nachlassRbd: tarifierungsVektorQuoteDb.rbdSparte,
    tarifbereich: tarifierungsVektorQuoteDb.tarifbereich,
    nachlassKombination: mapKombinachlass(tarifierungsVektorQuoteDb.kombinachlass),
    anzahlKombinationVertraege: tarifierungsVektorQuoteDb.kombinachlass,
    vertragsDauer: tarifierungsVektorQuoteDb.laufZeitSparte * 12,
    zahlungsperiode: tarifierungsVektorQuoteDb.zahlweise,
    selbstbehalt: tarifierungsVektorQuoteDb.selbstbeteiligung,
    jahresnetto: fachbaustein.nettopreisProJahr,
    bruttoZahlweise: fachbaustein.bruttopreis,
    cpStern: fachbaustein.cpStern,
    zahlungsart: tarifierungsVektorQuoteDb.zahlungsArt,
    bausteinInklusive: (fachbaustein as ZusatzoptionItem).isInclusive ?? false,
    anzahlFahrzeuge: tarifierungsVektorQuoteDb.fahrzeugeVersichert ?? null,
    fahrzeugArt: tarifierungsVektorQuoteDb.fahrzeugeArt,
    bausteinAusgewaehlt: produktlinie.selected && (fachbaustein as ZusatzoptionItem).isSelected,
  };

  if (isTaasVhvData(tarifierungsVektorQuoteDb?.taasSparteRequestModel?.taasSparte)) {
    const taasSparte = tarifierungsVektorQuoteDb.taasSparteRequestModel.taasSparte;
    baustein = {
      ...baustein,
      versicherungssummeRad:
        getSelectedVersicherungsSummeRadVhv(taasSparte, produktlinie.produktName) ?? undefined,
      versicherungssumme: getSelectedVersicherungsSummeVhv(taasSparte, produktlinie.produktName),
      entschaedigungsgrenzeWertsacheBetrag: getEntschaedigungsGrenzeWertsachenBetragVhv(
        taasSparte,
        produktlinie.produktName,
      ),
    };
  }

  return baustein;
}
